<template>
    <li>
        <div class="page-item" :class="{ active: node.id === selectedPage }"
            @click="$emit('select', node.id, node.name)">
            {{ node.name }}
            <button ref="toggleBtn" class="ellipsis-btn" @click.stop="toggleActions" aria-label="Actions">⋯</button>
            <div v-if="showActions" ref="menu" class="actions-menu">
                <button class="child-add-btn" @click.stop="onAddChild">Add</button>
                <button class="delete-page-menu-btn" @click.stop="onDeletePage">Delete</button>
                <button v-if="canMoveUp" class="move-up-btn" @click.stop="onMoveUp">↑ Up</button>
                <button v-if="canMoveDown" class="move-down-btn" @click.stop="onMoveDown">↓ Down</button>
            </div>
        </div>
        <ul v-if="node.children && node.children.length" class="child-list">
            <PageTree v-for="child in node.children" :key="child.id" :node="child" :siblings="node.children"
                :selectedPage="selectedPage" @select="(id, name) => $emit('select', id, name)"
                @addChild="$emit('addChild', $event)" @move-up="$emit('move-up', $event)"
                @move-down="$emit('move-down', $event)" @delete-page="$emit('delete-page', $event)" />
        </ul>
    </li>
</template>

<script>
export default {
    name: 'PageTree',
    props: ['node', 'selectedPage', 'siblings'],
    emits: ['select', 'addChild', 'move-up', 'move-down', 'delete-page'], // now 'select' emits (id, name)
    data() {
        return {
            showActions: false
        };
    },
    methods: {
        toggleActions() {
            // Notify other PageTree instances to close their menus
            document.dispatchEvent(new CustomEvent('page-tree-close-all', { detail: this }));
            this.showActions = !this.showActions;
        },
        onAddChild() {
            this.$emit('addChild', this.node);
            this.showActions = false;
        },
        onMoveUp() {
            this.$emit('move-up', this.node);
            this.showActions = false;
        },
        onMoveDown() {
            this.$emit('move-down', this.node);
            this.showActions = false;
        },
        onDeletePage() {
            this.$emit('delete-page', this.node);
            this.showActions = false;
        },
        onClickOutside(event) {
            if (this.showActions) {
                const menu = this.$refs.menu;
                const toggle = this.$refs.toggleBtn;
                if (
                    menu && !menu.contains(event.target) &&
                    toggle && !toggle.contains(event.target)
                ) {
                    this.showActions = false;
                }
            }
        },
        handleCloseAll(event) {
            if (event.detail !== this) {
                this.showActions = false;
            }
        },
    },
    computed: {
        index() {
            return this.siblings.findIndex(n => n.id === this.node.id);
        },
        canMoveUp() {
            return this.index > 0;
        },
        canMoveDown() {
            return this.index >= 0 && this.index < this.siblings.length - 1;
        }
    },
    mounted() {
        document.addEventListener('click', this.onClickOutside);
        document.addEventListener('page-tree-close-all', this.handleCloseAll);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onClickOutside);
        document.removeEventListener('page-tree-close-all', this.handleCloseAll);
    },
}
</script>

<style scoped>
/* --- Existing styles --- */
.page-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    position: relative;
}

.page-item:hover {
    background-color: #F2F4F7;
}

.page-item.active {
    background-color: #F4F3FF;
    color: var(--purple);
    font-weight: 600;
}

.page-item .actions {
    margin-left: auto;
    display: none;
}

.page-item:hover .actions {
    display: block;
}

.page-item .actions button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-secondary);
}


/* Remove hover-reveal for add/move buttons; now in menu */

.child-list {
    list-style: none;
    margin-left: 1rem;
    padding-left: 0;
}

/* --- Updated styles for ellipsis and actions menu --- */
.ellipsis-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    margin-left: auto;
    padding: 4px;
    font-size: 1.25rem;
    color: #6b7280;
    transition: color 0.2s ease, background-color 0.2s ease;
    border-radius: 4px;
    opacity: 0;
}

.page-item:hover .ellipsis-btn {
    opacity: 1;
}

.ellipsis-btn:hover {
    color: #111827;
    background-color: rgba(0, 0, 0, 0.05);
}

.actions-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    padding: 4px 0;
    display: flex;
    flex-direction: column;
    min-width: 112px;
    z-index: 1000;
}

.actions-menu button {
    padding: 8px 12px;
    font-size: 0.875rem;
    text-align: left;
    color: #111827;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.actions-menu button:not(:last-child) {
    margin-bottom: 4px;
}

.actions-menu button:hover {
    background-color: #f0f4f8;
}

.actions-menu .delete-page-menu-btn {
    color: #dc2626;
}

.actions-menu .delete-page-menu-btn:hover {
    background-color: #fef2f2;
    color: #b91c1c;
}
</style>
